import { useEffect, useRef, useState, useCallback } from "react";
import { Bot, Send, X, Loader2, LifeBuoy, MessageCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { walterChat, walterHistory } from "@/lib/api";
import { openUserbackWithQuestion, isUserbackReady } from "@/lib/userback";
import { toast } from "sonner";

const SESSION_KEY = "walter_session";

const SUGGESTIONS = [
  "Was kostet BeSca?",
  "Wie erfasse ich einen Beleg?",
  "Wie exportiere ich meine Belege?",
];

function newSessionId() {
  try {
    if (crypto?.randomUUID) return crypto.randomUUID();
  } catch {
    /* fallback */
  }
  return `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getSessionId() {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = newSessionId();
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return newSessionId();
  }
}

const GREETING = {
  role: "assistant",
  known: true,
  content:
    "Hallo, ich bin WALTER, Ihr Support-Assistent. Fragen Sie mich zu Funktionen, Bedienung, Preisen oder Ihrem Abo.",
};

export default function WalterWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastUnknown, setLastUnknown] = useState("");
  const loadedRef = useRef(false);
  const scrollRef = useRef(null);
  const sessionRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy, open]);

  const loadHistory = useCallback(async () => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    sessionRef.current = getSessionId();
    try {
      const data = await walterHistory(sessionRef.current);
      if (data?.messages?.length) {
        setMessages([GREETING, ...data.messages]);
        const lastBot = [...data.messages].reverse().find((m) => m.role === "assistant");
        if (lastBot && lastBot.known === false) {
          const lastUser = [...data.messages].reverse().find((m) => m.role === "user");
          setLastUnknown(lastUser?.content || "");
        }
      }
    } catch {
      /* still – Begrüßung bleibt */
    }
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadHistory();
  };

  const send = useCallback(
    async (text) => {
      const msg = (text ?? input).trim();
      if (!msg || busy) return;
      sessionRef.current = sessionRef.current || getSessionId();
      setInput("");
      setLastUnknown("");
      setMessages((m) => [...m, { role: "user", content: msg }]);
      setBusy(true);
      try {
        const res = await walterChat(sessionRef.current, msg);
        if (res?.session_id) {
          sessionRef.current = res.session_id;
          try {
            localStorage.setItem(SESSION_KEY, res.session_id);
          } catch {
            /* ignore */
          }
        }
        setMessages((m) => [
          ...m,
          { role: "assistant", content: res.answer, known: res.known },
        ]);
        if (res.known === false) setLastUnknown(res.suggested_question || msg);
      } catch {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content:
              "Entschuldigung, ich bin gerade nicht erreichbar. Bitte versuchen Sie es in Kürze erneut.",
            known: true,
          },
        ]);
      } finally {
        setBusy(false);
      }
    },
    [input, busy]
  );

  const contactTeam = async () => {
    const q = lastUnknown;
    const ok = isUserbackReady()
      ? await openUserbackWithQuestion(q, { quelle: "WALTER" })
      : false;
    if (ok) {
      toast.success("Feedback-Fenster geöffnet – Ihre Frage wurde kopiert und angehängt.");
    } else {
      const subject = encodeURIComponent("Frage an das BeSca-Team");
      const body = encodeURIComponent(q ? `Meine Frage: ${q}` : "");
      window.location.href = `mailto:office@besca.eu?subject=${subject}&body=${body}`;
    }
  };

  if (!user || user.role === "admin") return null;

  return (
    <div className="hidden md:block">
      {open && (
        <div
          className="fixed bottom-24 right-5 z-[70] flex h-[520px] w-[380px] flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-2xl"
          data-testid="walter-panel"
        >
          {/* Header */}
          <div className="flex items-center gap-3 bg-secondary px-4 py-3 text-secondary-foreground">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <Bot className="h-5 w-5" />
            </div>
            <div className="flex-1 leading-tight">
              <div className="text-sm font-semibold">WALTER</div>
              <div className="text-xs opacity-80">Ihr Support-Assistent</div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="rounded-md p-1 transition-colors hover:bg-white/10"
              aria-label="Schließen"
              data-testid="walter-close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 space-y-3 overflow-y-auto bg-muted/40 px-4 py-4"
            data-testid="walter-messages"
          >
            {messages.map((m, i) =>
              m.role === "user" ? (
                <div key={i} className="flex justify-end" data-testid="walter-msg-user">
                  <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-primary px-3 py-2 text-sm text-primary-foreground">
                    {m.content}
                  </div>
                </div>
              ) : (
                <div key={i} className="flex justify-start" data-testid="walter-msg-bot">
                  <div className="max-w-[85%] whitespace-pre-line rounded-2xl rounded-bl-sm border border-border bg-background px-3 py-2 text-sm text-foreground">
                    {m.content}
                  </div>
                </div>
              )
            )}

            {busy && (
              <div className="flex justify-start" data-testid="walter-typing">
                <div className="flex items-center gap-1 rounded-2xl rounded-bl-sm border border-border bg-background px-3 py-2.5">
                  <span className="h-2 w-2 animate-bounce rounded-full bg-primary [animation-delay:-0.2s]" />
                  <span className="h-2 w-2 animate-bounce rounded-full bg-primary [animation-delay:-0.1s]" />
                  <span className="h-2 w-2 animate-bounce rounded-full bg-primary" />
                </div>
              </div>
            )}

            {lastUnknown && !busy && (
              <div className="flex justify-start">
                <button
                  onClick={contactTeam}
                  className="inline-flex items-center gap-2 rounded-full bg-secondary px-3 py-2 text-sm font-medium text-secondary-foreground transition-colors hover:bg-primary hover:text-primary-foreground"
                  data-testid="walter-contact-team"
                >
                  <LifeBuoy className="h-4 w-4" />
                  Frage an das Team senden
                </button>
              </div>
            )}

            {messages.length <= 1 && !busy && (
              <div className="flex flex-wrap gap-2 pt-1">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => send(s)}
                    className="rounded-full border border-border bg-background px-3 py-1.5 text-xs text-foreground transition-colors hover:border-primary hover:text-primary"
                    data-testid={`walter-suggestion-${i}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="flex items-center gap-2 border-t border-border bg-background px-3 py-3"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ihre Frage an WALTER …"
              className="h-10 flex-1 rounded-full border border-border bg-white px-4 text-sm outline-none focus:border-primary"
              data-testid="walter-input"
              disabled={busy}
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity disabled:opacity-40"
              data-testid="walter-send"
              aria-label="Senden"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </form>
        </div>
      )}

      {/* Launcher */}
      <button
        onClick={toggle}
        className="fixed bottom-5 right-5 z-[70] flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-xl transition-transform hover:scale-105 active:scale-95"
        data-testid="walter-launcher"
        aria-label="WALTER Support öffnen"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>
    </div>
  );
}
